// client/src/components/stats/foldTo3betMatchupBenchmarks.ts
//
// Benchmarks de Fold to 3Bet por matchup posicional (hero como opener
// enfrentando un 3bet). Source: theoretical.
//
// La key es `${heroPos}_vs_${threebettorPos}` — ej: "BTN_vs_SB" significa
// que hero abrió desde BTN y enfrentó un 3bet desde SB.
//
// Lectura general:
// - Defendés MENOS (foldeás MÁS) cuando el 3bettor está IP que cuando está OOP
// - Defendés MÁS desde late position (opens más wide → más continuation hands)
// - Defendés MENOS desde early position (range value-heavy → ya pusiste tu mejor)
// - Las blinds suelen 3betear más light → debés defender más
// - Late opens vs blinds: defender ~50-65% (rango wide, mucho que continuar)
// - Early opens vs blinds: defender ~55-70% (range fuerte, no podés foldear value)

import { MatchupKey } from '../../utils/pokerStats/types';
import { Benchmark } from './benchmarks';
import { StackKey } from './leakRanges';

const THEORETICAL_FOLD_TO_3BET_BENCHMARKS_DEEP: Partial<Record<MatchupKey, Benchmark>> = {
  // Hero EP enfrentando 3bet — range tight, foldea menos value pero menos bluffs
  'EP_vs_BB':  { min: 40, max: 55, source: 'theoretical' },
  'EP_vs_SB':  { min: 42, max: 57, source: 'theoretical' },
  'EP_vs_BTN': { min: 45, max: 60, source: 'theoretical' },
  'EP_vs_CO':  { min: 43, max: 58, source: 'theoretical' },
  'EP_vs_HJ':  { min: 42, max: 57, source: 'theoretical' },

  // Hero HJ enfrentando 3bet
  'HJ_vs_BB':  { min: 45, max: 60, source: 'theoretical' },
  'HJ_vs_SB':  { min: 47, max: 62, source: 'theoretical' },
  'HJ_vs_BTN': { min: 50, max: 65, source: 'theoretical' },
  'HJ_vs_CO':  { min: 48, max: 63, source: 'theoretical' },

  // Hero CO enfrentando 3bet — open más wide, defender más
  'CO_vs_BB':  { min: 48, max: 62, source: 'theoretical' },
  'CO_vs_SB':  { min: 50, max: 64, source: 'theoretical' },
  'CO_vs_BTN': { min: 52, max: 67, source: 'theoretical' },

  // Hero BTN enfrentando 3bet — open wider, IP en 3bet pots, defender más
  'BTN_vs_BB': { min: 50, max: 65, source: 'theoretical' },
  'BTN_vs_SB': { min: 52, max: 67, source: 'theoretical' },

  // Hero SB enfrentando 3bet de BB — situación HU específica
  'SB_vs_BB':  { min: 50, max: 65, source: 'theoretical' },
};

// Short stack: cuando el 3bet es mayoritariamente shove, defendés menos en
// general (la pot odds importan más que la playability), pero MUCHO menos
// vs early/tight ranges.
const THEORETICAL_FOLD_TO_3BET_BENCHMARKS_SHORT: Partial<Record<MatchupKey, Benchmark>> = {
  // Vs blinds shoves: defender menos porque el shove range es más wide
  'EP_vs_BB':  { min: 30, max: 45, source: 'theoretical' },
  'EP_vs_SB':  { min: 32, max: 47, source: 'theoretical' },
  'EP_vs_BTN': { min: 35, max: 50, source: 'theoretical' },
  'EP_vs_CO':  { min: 32, max: 47, source: 'theoretical' },
  'EP_vs_HJ':  { min: 32, max: 47, source: 'theoretical' },

  'HJ_vs_BB':  { min: 35, max: 50, source: 'theoretical' },
  'HJ_vs_SB':  { min: 37, max: 52, source: 'theoretical' },
  'HJ_vs_BTN': { min: 38, max: 53, source: 'theoretical' },
  'HJ_vs_CO':  { min: 36, max: 51, source: 'theoretical' },

  'CO_vs_BB':  { min: 38, max: 52, source: 'theoretical' },
  'CO_vs_SB':  { min: 40, max: 54, source: 'theoretical' },
  'CO_vs_BTN': { min: 42, max: 56, source: 'theoretical' },

  'BTN_vs_BB': { min: 38, max: 53, source: 'theoretical' },
  'BTN_vs_SB': { min: 40, max: 55, source: 'theoretical' },

  'SB_vs_BB':  { min: 40, max: 55, source: 'theoretical' },
};

export const FOLD_TO_3BET_MATCHUP_BENCHMARKS: Record<
  StackKey,
  Partial<Record<MatchupKey, Benchmark>>
> = {
  short: THEORETICAL_FOLD_TO_3BET_BENCHMARKS_SHORT,
  mid:   THEORETICAL_FOLD_TO_3BET_BENCHMARKS_DEEP,
  deep:  THEORETICAL_FOLD_TO_3BET_BENCHMARKS_DEEP,
  all:   THEORETICAL_FOLD_TO_3BET_BENCHMARKS_DEEP,
};

export function getFoldTo3betMatchupBenchmark(
  matchup: MatchupKey,
  stack: StackKey
): Benchmark | null {
  const stackBenchmarks =
    FOLD_TO_3BET_MATCHUP_BENCHMARKS[stack] || FOLD_TO_3BET_MATCHUP_BENCHMARKS.all;
  return stackBenchmarks[matchup] || null;
}
